import { useEffect, useState } from "react";
import { Menu, X, Download } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";
import { agreementDownloadUrl } from "@/lib/api";

const LINKS = [
  { id: "about", label: "About Us" },
  { id: "coverage", label: "Coverage" },
  { id: "savings", label: "50% Savings" },
  { id: "testimonials", label: "Testimonials" },
  { id: "faq", label: "FAQ" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 50 : 0);
  };

  const toTop = (e) => {
    e.preventDefault();
    setOpen(false);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: false });
    else window.scrollTo(0, 0);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_24px_rgba(15,23,42,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="/" onClick={toTop} data-testid="nav-logo-link" className="shrink-0">
          <Logo />
        </a>
        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-600 transition-colors hover:text-brand-blue"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={agreementDownloadUrl}
            data-testid="nav-download-agreement"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/60 px-4 py-2 text-sm font-semibold text-brand-navy transition-all hover:border-brand-blue hover:text-brand-blue"
          >
            <Download className="h-4 w-4" /> Agreement
          </a>
          <button
            data-testid="nav-cta-quote"
            onClick={() => go("quote")}
            className="rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5 hover:bg-brand-bluedeep"
          >
            Get a Quote
          </button>
        </div>
        <button
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/70 text-brand-navy lg:hidden"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200/70 bg-white/95 px-4 pb-6 pt-2 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="border-b border-slate-100 py-3 text-left text-base font-medium text-slate-700"
              >
                {l.label}
              </button>
            ))}
            <div className="mt-4 flex flex-col gap-3">
              <a
                href={agreementDownloadUrl}
                data-testid="nav-mobile-download-agreement"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-brand-navy"
              >
                <Download className="h-4 w-4" /> Download Agreement
              </a>
              <button
                data-testid="nav-mobile-cta-quote"
                onClick={() => go("quote")}
                className="rounded-full bg-brand-blue px-5 py-3 text-sm font-semibold text-white"
              >
                Get a Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
